import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { useCourseContext } from "@/contexts/CourseContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import LearnerContentView from "@/components/LearnerContentView";
import TutorView from "@/components/TutorView";
import VoiceTutorView from "@/components/VoiceTutorView";
import FlashcardsView from "@/components/FlashcardsView";
import ExamView from "@/components/ExamView";
import CoachPanel from "@/components/CoachPanel";
import { ArrowLeft, Zap, StickyNote, X, Bot, BookOpen, GraduationCap, FileText, Layers, Mic } from "lucide-react";

export default function LearnNodeContent() {
  const { id: courseId, nodeId } = useParams<{ id: string; nodeId: string }>();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const designerPreview = searchParams.get("preview") === "designer";
  const activeTab = searchParams.get("tab") ?? "read";
  const { setCourseContext } = useCourseContext();
  const navigate = useNavigate();
  const [session, setSession] = useState<any>(null);
  const nodeFromState = (location.state as { node?: { title: string; is_key_node: boolean } })?.node;
  const [node, setNode] = useState<{ title: string; is_key_node: boolean } | null>(nodeFromState ?? null);
  const [loading, setLoading] = useState(!nodeFromState);
  const [notesPanelOpen, setNotesPanelOpen] = useState(false);
  const [coachPanelOpen, setCoachPanelOpen] = useState(false);
  const notesPanelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (!session) navigate("/auth");
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s);
      if (!s) navigate("/auth");
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  // Sync node from router state when navigating between nodes (avoids stale title)
  useEffect(() => {
    if (nodeFromState) {
      setNode(nodeFromState);
      setLoading(false);
    } else if (nodeId) {
      setNode(null);
      setLoading(true);
    }
  }, [nodeId, nodeFromState]);

  // Sync node from router state when navigating between nodes (avoids stale title)
  useEffect(() => {
    if (nodeFromState) {
      setNode(nodeFromState);
      setLoading(false);
    } else if (nodeId) {
      setNode(null);
      setLoading(true);
    }
  }, [nodeId, nodeFromState]);

  // Sync node from router state when navigating between nodes (avoids stale title)
  useEffect(() => {
    if (nodeFromState) {
      setNode(nodeFromState);
      setLoading(false);
    } else if (nodeId) {
      setNode(null);
      setLoading(true);
    }
  }, [nodeId, nodeFromState]);

  useEffect(() => {
    if (nodeId && session && !nodeFromState) {
      supabase
        .from("skill_nodes")
        .select("title, is_key_node")
        .eq("id", nodeId)
        .single()
        .then(({ data }) => {
          setNode(data);
        })
        .finally(() => setLoading(false));
    }
  }, [nodeId, session, nodeFromState]);

  useEffect(() => {
    if (courseId && nodeId && node) {
      setCourseContext({
        courseId,
        courseName: null,
        nodeId,
        nodeName: node.title,
        pageType: "learner",
      });
    }
  }, [courseId, nodeId, node, setCourseContext]);

  const handleBack = () => {
    // Use history back so we don't push a duplicate skill tree entry (avoids loop in designer preview)
    navigate(-1);
  };

  const handleProgressUpdate = () => {
    handleBack();
  };

  if (!session || loading || !nodeId || !courseId) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Minimal sticky header */}
      <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <Button variant="ghost" size="sm" onClick={handleBack} className="shrink-0">
            <ArrowLeft className="h-4 w-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">{designerPreview ? "Back to Course" : "Back to Learning Path"}</span>
          </Button>
          <h1 className="text-lg font-semibold truncate flex-1 text-center">
            {node?.title || "Lesson"}
          </h1>
          <div className="shrink-0 flex items-center gap-2">
            <Button
              variant={notesPanelOpen ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setNotesPanelOpen((o) => !o)}
              className="gap-1.5"
            >
              <StickyNote className="h-4 w-4" />
              Notes
            </Button>
            {!designerPreview && courseId && (
              <Button
                variant={coachPanelOpen ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setCoachPanelOpen(true)}
                className="gap-1.5"
              >
                <Bot className="h-4 w-4" />
                Coach
              </Button>
            )}
            {designerPreview && (
              <Badge variant="secondary" className="gap-1 bg-amber-500/20 text-amber-800 dark:text-amber-200">
                <Zap className="h-3 w-3" />
                Preview
              </Badge>
            )}
          </div>
        </div>
      </header>

      {/* Coach Panel (slide-out drawer) */}
      {!designerPreview && courseId && (
        <CoachPanel
          open={coachPanelOpen}
          onOpenChange={setCoachPanelOpen}
          courseId={courseId}
          courseTitle={node?.title ? `${node.title} — Course` : "Course"}
          nodeId={nodeId}
        />
      )}

      {/* Right-side notes panel - full overlay on mobile, side panel on desktop */}
      {notesPanelOpen && (
        <div
          ref={notesPanelRef}
          className="fixed top-14 right-0 z-30 w-full sm:max-w-md h-[calc(100vh-3.5rem)] border-l bg-background shadow-xl flex flex-col"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <span className="font-medium flex items-center gap-2">
              <StickyNote className="h-4 w-4" />
              My Notes
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setNotesPanelOpen(false)}
              aria-label="Close notes"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex-1 overflow-hidden min-h-0" id="learn-notes-panel-content" />
        </div>
      )}

      {/* Tab bar */}
      {!designerPreview && (
        <div className="sticky top-[57px] z-30 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="container mx-auto px-4 flex gap-0">
            {[
              { key: "read",       label: "Read",        Icon: FileText },
              { key: "tutor",      label: "AI Tutor",    Icon: BookOpen },
              { key: "voice",      label: "Voice",       Icon: Mic },
              { key: "flashcards", label: "Flashcards",  Icon: Layers },
              { key: "exam",       label: "Mock quiz",   Icon: GraduationCap },
            ].map(({ key, label, Icon }) => (
              <button
                key={key}
                onClick={() => setSearchParams(p => { const n = new URLSearchParams(p); n.set("tab", key); return n; })}
                className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === key
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Full-screen scrollable content; shrink when notes open on desktop */}
      <main
        className={`transition-[margin] ${
          notesPanelOpen ? "sm:mr-[28rem]" : ""
        } ${activeTab === "tutor" || activeTab === "voice" ? "" : "min-h-[calc(100vh-56px)] pt-4 sm:pt-8 px-4"}`}
      >
        {activeTab === "tutor" ? (
          <TutorView
            skillNodeId={nodeId}
            courseId={courseId ?? undefined}
            nodeTitle={node?.title || ""}
          />
        ) : activeTab === "voice" ? (
          <VoiceTutorView
            skillNodeId={nodeId}
            courseId={courseId ?? undefined}
            nodeTitle={node?.title || ""}
          />
        ) : activeTab === "flashcards" ? (
          <FlashcardsView
            skillNodeId={nodeId}
            courseId={courseId ?? undefined}
            nodeTitle={node?.title || ""}
          />
        ) : activeTab === "exam" ? (
          <ExamView
            skillNodeId={nodeId}
            courseId={courseId ?? undefined}
            nodeTitle={node?.title || ""}
            onProgressUpdate={handleProgressUpdate}
          />
        ) : (
          <LearnerContentView
            skillNodeId={nodeId}
            courseId={courseId ?? undefined}
            nodeTitle={node?.title || ""}
            isKeyNode={node?.is_key_node ?? false}
            onProgressUpdate={handleProgressUpdate}
            onBack={handleBack}
            notesPanelOpen={notesPanelOpen}
            onNotesPanelOpenChange={setNotesPanelOpen}
            notesPanelContainerId="learn-notes-panel-content"
          />
        )}
      </main>
    </div>
  );
}
